// src/oura/ui/Rows.tsx
//
// The list pieces every card is built from: a row, the head of a group of
// rows, a group that folds by its head, the head of a section with a firm
// rule under it, "Show N more", the grey inner stat, and the Open icon a
// doc row carries.
//
// API added 29 Sep 2026 (new export, nothing else changed):
//   RowOpenLink({ href, label, tip?, from?, align? })
//       The staff DocRow's Open icon, moved here as-is so the merchant doc
//       row can carry it too. It shows on hover and keyboard focus, keeps its
//       space when hidden, and sits above the row's stretched title button
//       (z-10). The row must be a `group`. `label` is the whole accessible
//       name ("Open in Google Docs: S4 Pricing page"); `tip` the hover text.
//       from "container" (default): shows once the row, a size container
//       ([container-type:inline-size]), is 40rem wide, which is what
//       GroupFold rowEnd="open" lines its note up with. "viewport": from 640px.
//       align "top" (default) for an items-start row, "center" for items-center.
//   GroupFold `tags?: ReactNode` (opt in, no default changed): tags drawn
//       after the head's chevron, visible open and folded ("This month").
//   GroupFold `wrap?: boolean` (opt in, no default changed): on a head under
//       30rem wide, the tags drop under the title and the folded note takes
//       its own line under it, instead of squeezing a long title into the
//       chevron (the plan's "Every month, from month 2" on a phone).
//   GroupFold `mark?: ReactNode` (opt in, no default changed): drawn in the
//       lead box in place of the dot, for a group that is not a state or a
//       period (the plan's change log carries its pen icon).

import { useContext, useId, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon, ExternalLinkIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { Dot } from "./Chips";
import { FoldScopeContext, foldKey, useFoldReveal, useFoldState } from "./foldState";
import { DOT_TONE, FOCUS_RING, INSET_HAIRLINE, LATE_TEXT, LEAD_W, ROW_HOVER, SURFACE, TAP_TARGET, TYPE, type RowLead, type Tone } from "./tokens";

export interface ListRowProps {
    /** Leading visual: a Dot, WorkKindIcon, PanelIcon or IconSquare. */
    icon?: ReactNode;
    title: ReactNode;
    sub?: ReactNode;
    /** Right-hand status or date. Replaced by `late` when that is set. */
    meta?: ReactNode;
    /** Trailing element. When the row is a link or button it must not be a control itself. */
    right?: ReactNode;
    onClick?: () => void;
    to?: string;
    /** Not started: ink4 text. A late note stays red regardless. */
    muted?: boolean;
    /** A late note, in rose, in place of `meta`. */
    late?: ReactNode;
    /**
     * Opt in: the kind of leading visual. The icon sits in a box of that
     * kind's width, and the hairline above the row starts where its text
     * starts (INSET_HAIRLINE) instead of running under the icon.
     */
    lead?: RowLead;
    /** Opt in: tags and chips, between the text and `meta`. */
    tags?: ReactNode;
    /**
     * Opt in: the row reads its own width, so it stacks in a narrow column
     * as it does on a phone. Under 36rem `tags` drop to the last line of the
     * text; under 22rem `meta` or `late` follow them. Beside the text they
     * would squeeze the title to a word.
     */
    stackMeta?: boolean;
    /**
     * Opt in: the row's status, for a screen reader only, when the group
     * head above says it and the row does not: read as "Title, Needs us".
     */
    srStatus?: string;
    /**
     * Opt in: the row's `right` holds its one action (an ArrowLink with
     * `stretch`), and the whole row is that action, with the row hover.
     * For a row whose other controls must keep their own click (ROW_OWN),
     * where `to`/`onClick` would nest them inside a link or button.
     */
    stretch?: boolean;
    className?: string;
    /** `data-*` attributes go onto the row itself, so a screen can find it again after a re-render. */
    [data: `data-${string}`]: string | undefined;
}

/**
 * One row: 48px minimum, a hairline above every row but the first. The title
 * runs to two lines before it is cut, so an ask or a rival's name reads whole
 * on a phone; a string title carries the full text as its tooltip.
 *
 * Rows are direct siblings in their list (no <li> around each one), because
 * the "no line above the first" rule reads the row's position among them.
 * A row with `to` or `onClick` is one link or button, chevron included.
 */
export function ListRow({ icon, title, sub, meta, right, onClick, to, muted, late, lead, tags, stackMeta, srStatus, stretch, className, ...data }: ListRowProps) {
    const interactive = Boolean(to || onClick || stretch);
    // With a line under the title, the facts on the right sit level with its
    // first line, as the icon does.
    const top = Boolean(lead && sub);
    const fact = late || meta;
    const factTone = late ? LATE_TEXT : muted ? "text-gray-400" : "text-gray-500";
    // Stacked, the tags (under 36rem) and the fact (under 22rem) are a line
    // under the title: the icon stays level with the title's first line.
    // Literal classes, so the JIT sees each one.
    const stackTags = Boolean(stackMeta && tags);
    const stackFact = Boolean(stackMeta && fact);
    const body = (
        <>
            {icon && (
                <span
                    className={cn(
                        "flex shrink-0 items-center",
                        lead && cn("justify-center", LEAD_W[lead]),
                        Boolean(sub) && "mt-1.5 self-start",
                        !sub && stackTags && "h-5 self-start [@container(min-width:36rem)]:h-auto [@container(min-width:36rem)]:self-auto",
                        !sub && !stackTags && stackFact && "h-5 self-start [@container(min-width:22rem)]:h-auto [@container(min-width:22rem)]:self-auto",
                    )}
                >
                    {icon}
                </span>
            )}
            <span className="min-w-0 flex-1">
                <span
                    title={typeof title === "string" ? title : undefined}
                    className={cn(
                        TYPE.tb,
                        "line-clamp-2 break-words",
                        muted ? "text-gray-400" : "text-gray-900",
                        interactive && "group-hover:text-blue-600",
                    )}
                >
                    {title}
                    {srStatus && <span className="sr-only">, {srStatus}</span>}
                </span>
                {sub && <span className={cn(TYPE.tc, "block", muted ? "text-gray-400" : "text-gray-500")}>{sub}</span>}
                {(stackTags || stackFact) && (
                    <span
                        className={cn(
                            "mt-1 flex flex-wrap items-center gap-1.5",
                            stackTags ? "[@container(min-width:36rem)]:hidden" : "[@container(min-width:22rem)]:hidden",
                        )}
                    >
                        {tags}
                        {fact && <span className={cn(TYPE.tc, factTone, "[@container(min-width:22rem)]:hidden")}>{fact}</span>}
                    </span>
                )}
            </span>
            {tags && (
                <span className={cn("shrink-0 items-center gap-1.5", stackMeta ? "hidden [@container(min-width:36rem)]:flex" : "flex", top && "self-start")}>
                    {tags}
                </span>
            )}
            {late ? (
                <span
                    className={cn(TYPE.tc, LATE_TEXT, "shrink-0 whitespace-nowrap", stackMeta && "hidden [@container(min-width:22rem)]:inline", top && "self-start pt-px")}
                >
                    {late}
                </span>
            ) : (
                meta && (
                    <span
                        className={cn(
                            TYPE.tc,
                            "shrink-0 whitespace-nowrap",
                            muted ? "text-gray-400" : "text-gray-500",
                            stackMeta && "hidden [@container(min-width:22rem)]:inline",
                            top && "self-start pt-px",
                        )}
                    >
                        {meta}
                    </span>
                )
            )}
            {right}
            {(to || onClick) && <ChevronRightIcon aria-hidden size={14} className="shrink-0 self-center text-gray-400" />}
        </>
    );
    // A stacking row is its own size container: its parts read its width.
    const shell = lead
        ? cn("flex min-h-12 w-full items-center gap-3 py-2 text-left", INSET_HAIRLINE(lead), stackMeta && "[container-type:inline-size]", className)
        : cn("flex min-h-12 w-full items-center gap-3 border-t py-2 text-left first:border-t-0", SURFACE.hairline, stackMeta && "[container-type:inline-size]", className);

    if (to) {
        return (
            <Link to={to} className={cn(shell, ROW_HOVER, FOCUS_RING)} {...data}>
                {body}
            </Link>
        );
    }
    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={cn(shell, ROW_HOVER, FOCUS_RING)} {...data}>
                {body}
            </button>
        );
    }
    return (
        <div className={cn(shell, stretch && ROW_HOVER)} {...data}>
            {body}
        </div>
    );
}

/** The head of a group of rows: dot, title, count. Deliberately not row-shaped. */
export function GroupHead({ tone, title, count, right }: { tone: Tone; title: ReactNode; count: number; right?: ReactNode }) {
    return (
        <div className="flex items-center gap-2 pb-1 pt-3 first:pt-0">
            <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", DOT_TONE[tone])} />
            <span className={cn(TYPE.tc, "font-semibold text-gray-900")}>{title}</span>
            <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{count}</span>
            {right && <span className={cn(TYPE.tc, "ml-auto text-gray-500")}>{right}</span>}
        </div>
    );
}

/** The group heading's type: 17px (the TYPE.tt size) at weight 500, one notch under a card title. */
const GROUP_HEAD_TITLE = "text-[17px] leading-6 font-medium tracking-[-0.01em] text-gray-900";
const GROUP_HEAD_COUNT = "text-[17px] leading-6 tabular-nums text-gray-400";

/**
 * A group of rows that folds by its head: dot, title, count and a chevron,
 * with a firm divider above every group but the first. Put a card's groups
 * as the only children of one wrapper, so the first loses its divider.
 *
 * The dot sits in the rows' lead box, so the title starts where their text
 * does. Folded, the head can carry a `note` ("1 with a red flag · 1 late"),
 * so a folded group never hides what someone must act on; it ends where the
 * rows' facts end. `useFoldOpener()(id)` opens it in place (no scroll, no
 * focus move), mounted or not, as it does a card.
 *
 * A group that starts folded remembers being opened, under its card's
 * FoldScope. A group that starts open (the ones someone acts on) forgets a
 * hand fold once the screen goes: folded on one client, it would hide what
 * needs us on the next.
 */
export function GroupFold({
    id,
    tone,
    title,
    count,
    lead,
    defaultFolded = false,
    remember = defaultFolded,
    note,
    rowEnd = "chevron",
    tags,
    wrap = false,
    mark,
    size = "md",
    children,
}: {
    id: string;
    tone: Tone;
    title: ReactNode;
    count: number;
    lead: RowLead;
    defaultFolded?: boolean;
    /** Keep a hand fold across visits. By default only a group that starts folded does. */
    remember?: boolean;
    /** Shown on the head only while it is folded. */
    note?: ReactNode;
    /**
     * What the rows keep right of their fact, so the note ends where the
     * facts do: a chevron (ListRow), or the Open icon and a chevron (the
     * documents board, from 40rem wide).
     */
    rowEnd?: "chevron" | "open";
    /**
     * Opt in (29 Sep 2026): tags drawn after the chevron, visible open and
     * folded, for a state the whole group shares ("This month" on the plan).
     */
    tags?: ReactNode;
    /**
     * Opt in (29 Sep 2026): a head under 30rem wide wraps. The tags drop
     * under the title and the folded note takes its own line, starting where
     * the title does, so a long title never runs into the chevron.
     */
    wrap?: boolean;
    /**
     * Opt in (29 Sep 2026): a small icon drawn where the dot sits, for a group
     * that is not a state or a period (a change log), so it does not read as
     * one more of them. Decorative: pass it aria-hidden.
     */
    mark?: ReactNode;
    /**
     * Opt in (16 Oct 2026): "sm" for a group inside a section of a card (Your
     * requests in the account manager card), where a card-title-sized head
     * outranked the section label above it.
     */
    size?: "md" | "sm";
    children: ReactNode;
}) {
    const key = foldKey(useContext(FoldScopeContext), id);
    const titleCls = size === "sm" ? cn(TYPE.tb, "font-medium text-gray-700") : GROUP_HEAD_TITLE;
    const countCls = size === "sm" ? cn(TYPE.tc, "tabular-nums text-gray-400") : GROUP_HEAD_COUNT;
    const [folded, setFolded] = useFoldState(remember ? key : null, defaultFolded);
    const bodyId = useId();
    useFoldReveal(key, () => {
        if (folded) setFolded(false);
    });
    return (
        <div className={cn("border-t first:border-t-0", SURFACE.divider)}>
            {/* A size container, so the note's inset follows the rows' width as theirs does. */}
            <h3 className="m-0 [container-type:inline-size]">
                <button
                    type="button"
                    data-group-head={id}
                    aria-expanded={!folded}
                    aria-controls={bodyId}
                    onClick={() => setFolded(!folded)}
                    className={cn("flex w-full items-center gap-3 rounded-md pb-1 text-left", size === "sm" ? "min-h-9 pt-2" : "min-h-12 pt-3", wrap && "flex-wrap gap-y-0.5", FOCUS_RING)}
                >
                    <span className={cn("flex shrink-0 justify-center", LEAD_W[lead])}>
                        {mark ?? <Dot tone={tone} className="h-2.5 w-2.5" />}
                    </span>
                    {/* A heading, not a label: 17px like the card title, one weight lighter so
                        the card still leads (YW, 29 Sep 2026: "needs to be appropriately larger
                        to carry its UI as a header"). */}
                    {wrap ? (
                        // Under 30rem the tags take a line under the title, and the
                        // title wraps its words beside its count and chevron.
                        <span className="flex min-w-0 flex-1 flex-col items-start gap-1 [@container(min-width:30rem)]:flex-row [@container(min-width:30rem)]:items-center [@container(min-width:30rem)]:gap-2">
                            <span className="inline-flex min-w-0 items-center gap-2">
                                <span className={titleCls}>{title}</span>
                                <span className={countCls}>{count}</span>
                                <ChevronRightIcon
                                    aria-hidden
                                    size={16}
                                    className={cn("shrink-0 text-gray-400 transition-transform duration-200 motion-reduce:transition-none", !folded && "rotate-90")}
                                />
                            </span>
                            {tags && <span className="inline-flex shrink-0 items-center gap-1.5 [@container(min-width:30rem)]:ml-1">{tags}</span>}
                        </span>
                    ) : (
                        <span className="inline-flex min-w-0 items-center gap-2">
                            <span className={titleCls}>{title}</span>
                            <span className={countCls}>{count}</span>
                            <ChevronRightIcon
                                aria-hidden
                                size={16}
                                className={cn("shrink-0 text-gray-400 transition-transform duration-200 motion-reduce:transition-none", !folded && "rotate-90")}
                            />
                            {tags && <span className="ml-1 inline-flex shrink-0 items-center gap-1.5">{tags}</span>}
                        </span>
                    )}
                    {folded && note && (
                        <span
                            className={cn(
                                TYPE.tc,
                                "ml-auto min-w-0 truncate pr-[26px]",
                                rowEnd === "open" && "[@container(min-width:40rem)]:pr-[66px]",
                                wrap && "basis-full pl-[26px] [@container(min-width:30rem)]:basis-auto [@container(min-width:30rem)]:pl-0",
                            )}
                        >
                            {note}
                        </span>
                    )}
                </button>
            </h3>
            <div id={bodyId} hidden={folded} className="pb-2">
                {!folded && children}
            </div>
        </div>
    );
}

/**
 * A section inside a card: heading, count, a note on the right, and a firm
 * grey rule under it (the Overview "Needs us" / "Needs the client" pattern).
 */
export function SectionHead({ title, count, summary, first }: { title: ReactNode; count?: number; summary?: ReactNode; first?: boolean }) {
    return (
        <div className={cn("flex items-baseline justify-between gap-3 border-b border-gray-300 pb-2", !first && "mt-6")}>
            <h3 className={cn(TYPE.tc, "m-0 font-semibold text-gray-900")}>
                {title}
                {count !== undefined && <span className="ml-1 font-normal tabular-nums text-gray-400">{count}</span>}
            </h3>
            {summary && <span className={cn(TYPE.tm, "text-gray-500")}>{summary}</span>}
        </div>
    );
}

/**
 * The rows past the first few, then the toggle under them, so the list reads
 * straight on and "Show fewer" sits where the list ends. The rows render as
 * siblings of whatever comes before, so the hairline rule carries across the
 * join. Renders nothing for 0.
 *
 * It holds its own open state. A screen that must keep it (to put a list
 * back as it was after Back) passes `open` and hears `onToggle`.
 */
export function ShowMore({
    count,
    children,
    open,
    onToggle,
}: {
    count: number;
    children: ReactNode;
    open?: boolean;
    onToggle?: (open: boolean) => void;
}) {
    const [own, setOwn] = useState(false);
    const isOpen = open ?? own;
    if (count <= 0) return null;
    return (
        <>
            {isOpen && children}
            <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => {
                    const next = !isOpen;
                    setOwn(next);
                    onToggle?.(next);
                }}
                className={cn(TYPE.tc, "mt-1 block rounded py-1.5 text-left font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)}
            >
                {isOpen ? "Show fewer" : `Show ${count} more`}
            </button>
        </>
    );
}

/** The grey stat block inside a card or drawer. */
export function InnerStat({ label, value, sub }: { label: ReactNode; value: ReactNode; sub?: ReactNode }) {
    return (
        <div className={SURFACE.inner}>
            <p className={cn(TYPE.lab, "m-0")}>{label}</p>
            <div className={cn(TYPE.tb, "mt-0.5 text-gray-900")}>{value}</div>
            {sub && <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-500")}>{sub}</p>}
        </div>
    );
}

/** From where the Open icon shows. Literal, so the JIT sees each class. */
const OPEN_FROM = { container: "[@container(min-width:40rem)]:grid", viewport: "sm:grid" } as const;

/**
 * The real file, as a small icon that shows on hover or keyboard focus. Its
 * space is always kept, so the row never shifts and the facts line up. On a
 * screen with no hover (a tablet) it stays out of reach, never an invisible
 * tap target over the row: the drawer's foot has the same link everywhere.
 */
export function RowOpenLink({
    href,
    label,
    tip,
    from = "container",
    align = "top",
}: {
    href: string;
    /** The whole accessible name: what opens, and where. */
    label: string;
    /** The hover text, e.g. "Open in Google Docs". */
    tip?: string;
    from?: "container" | "viewport";
    align?: "top" | "center";
}) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={tip}
            className={cn(
                "relative z-10 hidden h-7 w-7 shrink-0 place-items-center rounded-full text-gray-500 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-900 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none [@media(hover:none)]:invisible",
                align === "top" && "mt-0.5",
                OPEN_FROM[from],
                FOCUS_RING,
                TAP_TARGET,
            )}
        >
            <ExternalLinkIcon aria-hidden size={14} />
        </a>
    );
}
